import React from 'react';
    import { motion } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../common/SafeIcon';
    import Navigation from '../components/Navigation';
    import CosmicBackground from '../components/CosmicBackground';

    const { FiUsers, FiCalendar, FiBook, FiHeart, FiArrowRight, FiCheck, FiStar } = FiIcons;

    const ClarityClubPage = () => {
      const handleJoinClick = () => {
        window.open('https://discord.withjahx.com', '_blank');
      };

      const features = [
        {
          icon: FiUsers,
          title: 'Supportive Discord Community',
          description: 'Join a safe space where vulnerability is strength and everyone gets it.',
        },
        {
          icon: FiCalendar,
          title: 'Weekly Live Trainings & Co-working',
          description: 'Join live trainings on Thursdays and a free Q&A co-working session on Tuesdays,all hosted on Discord.',
        },
        {
          icon: FiBook,
          title: 'Resource Growth Generators',
          description: 'Access our library of templates and guides,plus all free and a limited selection of premium AI-powered prompt tools.',
        },
        {
          icon: FiHeart,
          title: 'Monthly Challenges',
          description: 'Gentle,achievable challenges that build momentum without adding overwhelm.',
        },
      ];

      const testimonials = [
        {
          name: 'Sarah M.',
          role: 'Creative Entrepreneur',
          text: "Finally,a community that understands. The weekly calls on Discord have been my lifeline.",
          rating: 5,
        },
        {
          name: 'Mike R.',
          role: 'Digital Creator',
          text: "The Resource Growth Generators alone are worth 10x the price. Game-changing templates.",
          rating: 5,
        },
        {
          name: 'Lisa K.',
          role: 'Solopreneur',
          text: "The Tuesday co-working sessions helped me go from scattered chaos to clear direction in just one month.",
          rating: 5,
        },
      ];

      return (
        <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
          <CosmicBackground />
          <Navigation />

          {/* Hero Section */}
          <section className="pt-32 pb-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-4xl mx-auto"
              >
                <div className="inline-flex items-center space-x-2 bg-green-900/30 px-4 py-2 rounded-full border border-green-500/30 mb-6">
                  <SafeIcon icon={FiUsers} className="text-green-400" />
                  <span className="text-sm font-poppins text-green-200">Community & Support</span>
                </div>
                <h1 className="text-5xl md:text-7xl font-orbitron font-bold mb-6 leading-tight">
                  <span className="clarity-gradient">Clarity Club (BFCC)</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 mb-8 font-poppins font-light max-w-3xl mx-auto leading-relaxed">
                  A sanctuary for overwhelmed creators. The best place to get your feet wet,get moving,and transform
                  burnout into breakthrough.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleJoinClick}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-4 rounded-full font-poppins font-semibold text-lg glow-button flex items-center space-x-2"
                  >
                    <span>Join for $5.55/mo or $60/yr</span>
                    <SafeIcon icon={FiArrowRight} />
                  </motion.button>
                  <div className="text-center">
                    <p className="text-gray-400 font-poppins text-sm">Cancel anytime • No contracts</p>
                    <div className="flex justify-center items-center space-x-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current text-sm" />
                      ))}
                      <span className="ml-2 text-gray-400 text-sm">4.9/5 from 2,847 members</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  What You Get <span className="clarity-gradient">Inside</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  Everything you need to move from overwhelm to clarity,without the overwhelm.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 gap-8 mb-16">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-8 rounded-2xl border border-gray-700/50 backdrop-blur-sm hover:border-green-500/30 transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-6">
                      <SafeIcon icon={feature.icon} className="text-white text-2xl" />
                    </div>
                    <h3 className="text-2xl font-orbitron font-bold mb-4 text-white">{feature.title}</h3>
                    <p className="text-gray-300 font-poppins leading-relaxed">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Real <span className="clarity-gradient">Stories</span>
                </h2>
              </motion.div>
              <div className="grid md:grid-cols-3 gap-8">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 p-8 rounded-2xl border border-gray-700/30 backdrop-blur-sm"
                  >
                    <div className="flex items-center space-x-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <blockquote className="text-gray-300 font-poppins mb-6 italic">"{testimonial.text}"</blockquote>
                    <div>
                      <p className="text-white font-poppins font-semibold">{testimonial.name}</p>
                      <p className="text-gray-400 font-poppins text-sm">{testimonial.role}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center bg-gradient-to-r from-green-900/40 to-emerald-900/40 p-12 rounded-3xl border border-green-500/30 backdrop-blur-sm max-w-4xl mx-auto"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Ready to Find Your <span className="clarity-gradient">Clarity</span>?
                </h2>
                <p className="text-xl text-gray-300 font-poppins mb-8 max-w-2xl mx-auto">
                  Join 2,847 creators who've transformed overwhelm into sustainable success. Included free with plans
                  $33/mo and higher.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center justify-center space-x-3 text-gray-300 font-poppins">
                    <SafeIcon icon={FiCheck} className="text-green-400" />
                    <span>Cancel anytime,no questions asked</span>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleJoinClick}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 px-12 py-4 rounded-full font-poppins font-semibold text-xl glow-button"
                >
                  Join for $5.55/mo or $60/yr
                </motion.button>
                <p className="text-gray-400 font-poppins text-sm mt-4">
                  Less than the cost of a fancy coffee. More valuable than years of therapy.
                </p>
              </motion.div>
            </div>
          </section>
        </div>
      );
    };

    export default ClarityClubPage;